/**
 * Pick the Hand Role — bank-style, 18 spots.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  HAND_ROLE_BANK,
  type HandRole,
  type HandRoleSpot,
  ROLE_LABEL,
} from "@/features/quiz/lib/handRoleQuiz";

const OPTIONS: HandRole[] = ["value", "bluff", "semi_bluff", "give_up"];

export function QuizHandRole() {
  const {
    question,
    revealedAnswer,
    isCorrect,
    correctAnswer,
    stats,
    submit,
    next,
    bankPosition,
    bankSize,
    isComplete,
    restart,
  } = useQuizSession<HandRoleSpot, HandRole>({
    quizType: "hand_role",
    bank: HAND_ROLE_BANK,
    getCorrectAnswer: (q) => q.role,
    getQuestionPayload: (q) => ({ hand: q.hand, board: q.board }),
  });

  return (
    <QuizLayout
      Icon={Brain}
      title="Pick the Hand Role"
      subtitle="Sheet 3: value / bluff / semi-bluff / give up"
      description={
        <>
          Tests sheet 3's hand-by-hand test: <em>what worse hand calls</em> +{" "}
          <em>what better hand folds</em>. The right role tells you whether to
          bet (value / bluff / semi-bluff) or check (give up). Most c-bet leaks
          come from misreading the role.
        </>
      }
      stats={stats}
      bankPosition={bankPosition}
      bankSize={bankSize}
      isComplete={isComplete}
      onRestart={restart}
    >
      <div className="rounded-xl border border-border bg-white/[0.02] p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-text-muted">
          Spot
        </div>
        <div className="text-sm text-text leading-relaxed">
          {question.context}
        </div>
        <div className="flex items-baseline gap-6 pt-1">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">
              Hero
            </div>
            <div className="font-mono text-lg text-text">{question.hand}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">
              Board
            </div>
            <div className="font-mono text-lg text-text">{question.board}</div>
          </div>
        </div>
      </div>

      <p className="text-center text-text text-base">
        What's the hand role here?
      </p>

      <div className="grid grid-cols-2 gap-3">
        {OPTIONS.map((role) => (
          <QuizOption
            key={role}
            value={role}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            {ROLE_LABEL[role]}
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{ROLE_LABEL[correctAnswer]}</strong>
            </>
          }
          rationale={question.rationale}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
